import { AlertMessage } from "@/components/ui/AlertMessage";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FormField } from "@/components/ui/form/FormField";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { SignInSchemaType } from "@/schemas/signIn.schema";
import { Link } from "@tanstack/react-router";
import { t } from "i18next";
import { LockIcon, Mail } from "lucide-react";
import type { UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";

interface Props {
  onSubmit: (data: SignInSchemaType) => void;
  isSubmitting: boolean;
  form: UseFormReturn<SignInSchemaType>;
}

export const SignInForm = (props: Props): React.ReactNode => {
  return (
    <Form {...props.form}>
      <form
        className="w-full flex flex-col"
        onSubmit={props.form.handleSubmit(props.onSubmit)}
      >
        <div className="space-y-4 mb-4">
          <div className="flex">
            <FormField
              control={props.form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full flex flex-col space-y-1">
                  <FormLabel>{t("login.form.emailLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="username"
                      icon={<Mail />}
                      placeholder={t("login.form.emailPlaceholder")}
                      type="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex">
            <FormField
              control={props.form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full flex flex-col space-y-1">
                  <FormLabel>{t("login.form.passwordLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      icon={<LockIcon />}
                      placeholder={t("login.form.passwordLabel")}
                      type="password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="text-right">
            <Link
              className="text-sm text-tinted-grey-3 transition-colors hover:underline"
              to="/forgot-password"
            >
              {t("login.form.forgotPassword")}
            </Link>
          </div>
        </div>
        <div className="space-y-3">
          {props.form.formState.errors.root?.message && (
            <AlertMessage
              description={props.form.formState.errors.root.message}
              title="Error"
              variant="destructive"
            />
          )}
          <Button
            className="w-full"
            disabled={props.isSubmitting}
            size={"xl"}
            type="submit"
          >
            {props.isSubmitting && <LoadingSpinner />}
            {t("login.form.signInButton")}
          </Button>
        </div>
      </form>
    </Form>
  );
};
